import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@refinedev/core';
import { Table } from 'antd';
import styles from './DataTable.module.scss';
import tableStyles from '../applications/edit/tableStyles.module.scss';
import TablePagination from '../ui/table-pagination';

interface IDataTableProps {
    data: any[];
    group?: string;
    pageSizeOptions?: Array<string | number>;
    pageSizeSelectorWidth?: number | string;
    [x: string]: any;
}

export const DataTable: FC<IDataTableProps> = ({
    data,
    group = 'applications.fields',
    className,
    scroll,
    pageSizeOptions,
    pageSizeSelectorWidth,
    ...props
}) => {
    const t = useTranslate();
    const yesno = (flag: boolean) => (flag ? 'yes' : 'no');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const defaultPageSizeOptions = ['10', '25', '50', '100'];
    const rawPageSizeOptions = (pageSizeOptions ?? defaultPageSizeOptions).map(
        option => option.toString()
    );

    const uniquePageSizeOptions = rawPageSizeOptions.reduce<string[]>(
        (acc, current) => {
            if (!acc.includes(current)) acc.push(current);
            return acc;
        },
        []
    );

    const orderedPageSizeOptions = defaultPageSizeOptions.filter(option =>
        uniquePageSizeOptions.includes(option)
    );

    uniquePageSizeOptions.forEach(option => {
        if (!orderedPageSizeOptions.includes(option)) {
            orderedPageSizeOptions.push(option);
        }
    });

    if (!orderedPageSizeOptions.includes(pageSize.toString())) {
        orderedPageSizeOptions.push(pageSize.toString());
    }

    const renderCell = (value: any) => {
        return Array.isArray(value) ? (
            <div>
                <pre className={styles.code}>
                    {JSON.stringify(value, null, 2)}
                </pre>
            </div>
        ) : (
            value
        );
    };

    const iteratedColumns: any[] = [];

    for (let k in data[0]) {
        iteratedColumns.push({
            title: t(`${group}.${k}`),
            dataIndex: k,
            render: (value: any) => {
                let subs = yesno(value);

                return typeof value === 'boolean'
                    ? t(`labels.${subs}`)
                    : renderCell(value);
            },
            className: 'align-top',
        });
    }

    const totalItems = data.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const currentPageSafe = Math.min(currentPage, totalPages);

    useEffect(() => {
        if (currentPage !== currentPageSafe) {
            setCurrentPage(currentPageSafe);
        }
    }, [currentPageSafe, currentPage]);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPageSafe - 1) * pageSize;
        return data.slice(startIndex, startIndex + pageSize);
    }, [currentPageSafe, data, pageSize]);

    const tableClassName = [tableStyles.table, className]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={tableStyles.tableWrapper}>
            <Table
                className={tableClassName}
                columns={iteratedColumns}
                dataSource={paginatedData}
                pagination={false}
                scroll={
                    scroll ?? {
                        x: 500,
                    }
                }
                {...props}
            />
            <TablePagination
                currentPage={currentPageSafe}
                pageSize={pageSize}
                totalItems={totalItems}
                pageSizeOptions={orderedPageSizeOptions}
                onChangePage={setCurrentPage}
                onChangePageSize={setPageSize}
                pageSizeSelectorWidth={pageSizeSelectorWidth}
                previousLabel={t('applications.pagination.previous', 'Попередня сторінка')}
                nextLabel={t('applications.pagination.next', 'Наступна сторінка')}
                showDocumentsLabel={t(
                    'applications.pagination.show_documents',
                    'Показувати документи'
                )}
                rangeLabel={(start, end, total) =>
                    `${start}-${end} ${t('applications.pagination.of_total', 'з')} ${total}`
                }
            />
        </div>
    );
};
